import { Helmet } from "react-helmet-async";

const defaults = {
  siteName: "LiveWifi",
  baseUrl: "https://livewifi.es",
  image: "https://livewifi.es/og-livewifi.jpg",
  locale: "es_ES",
  twitter: "@livewifi_es",
};

/**
 * Universal SEO component.
 *
 * Props:
 *  - title: Page title (will be suffixed with " | LiveWifi")
 *  - description: Meta description (150-165 chars ideal)
 *  - path: Canonical path (e.g. "/fibra-movil")
 *  - keywords: Optional comma-separated keywords (for legacy crawlers)
 *  - image: Optional OG/Twitter image absolute URL
 *  - noIndex: Set true for admin/private pages
 *  - jsonLd: Optional schema.org JSON-LD object or array of objects
 *  - alternates: Optional [{ hreflang: "en", href: "..." }] for multilingual pages
 *  - type: og:type (default "website")
 */
const SEO = ({
  title,
  description,
  path = "/",
  keywords,
  image,
  noIndex = false,
  jsonLd,
  alternates,
  type = "website",
}) => {
  const fullTitle = title
    ? `${title} | ${defaults.siteName}`
    : `${defaults.siteName} — Operador canario de fibra, móvil, TV y eSIM`;
  const url = `${defaults.baseUrl}${path}`;
  const ogImage = image || defaults.image;

  const ldArray = !jsonLd ? [] : Array.isArray(jsonLd) ? jsonLd : [jsonLd];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noIndex
        ? <meta name="robots" content="noindex,nofollow" />
        : <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />}

      {/* hreflang alternates */}
      {alternates && alternates.map((alt, i) => (
        <link key={i} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
      ))}
      {alternates && (
        <link rel="alternate" hrefLang="x-default" href={url} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={defaults.siteName} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={defaults.locale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={defaults.twitter} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Structured Data */}
      {ldArray.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
